if (!customElements.get('instagram-rail')) {
  customElements.define(
    'instagram-rail',
    class InstagramRail extends HTMLElement {
      connectedCallback() {
        this.rail = this.querySelector('[data-rail]');
        this.progress = this.querySelector('[data-progress]');
        this.arrows = [...this.querySelectorAll('[data-direction]')];
        if (!this.rail) return;

        this.pages = [];
        this.drag = null;

        this.onArrowClick = this.onArrowClick.bind(this);
        this.onDotClick = this.onDotClick.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onClickCapture = this.onClickCapture.bind(this);
        this.onDragStart = (event) => event.preventDefault();
        this.onTouchMove = (event) => event.stopPropagation();

        this.arrows.forEach((arrow) => arrow.addEventListener('click', this.onArrowClick));
        this.progress?.addEventListener('click', this.onDotClick);

        this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
        this.observer = new MutationObserver(() => {
          this.attach();
          this.scheduleLayout();
        });
        this.observer.observe(this.rail, { childList: true, subtree: true });
        this.attach();
      }

      disconnectedCallback() {
        this.arrows?.forEach((arrow) => arrow.removeEventListener('click', this.onArrowClick));
        this.progress?.removeEventListener('click', this.onDotClick);
        this.detach();
        this.observer?.disconnect();
        this.resizeObserver?.disconnect();
        cancelAnimationFrame(this.frame);
        cancelAnimationFrame(this.layoutFrame);
        clearTimeout(this.snapTimer);
      }

      attach() {
        const scroller = this.rail.querySelector('.instafeed-new-layout-container');
        if (!scroller || scroller === this.scroller) return;
        this.detach();
        this.scroller = scroller;
        this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
        this.scroller.addEventListener('pointerdown', this.onPointerDown);
        this.scroller.addEventListener('click', this.onClickCapture, true);
        this.scroller.addEventListener('dragstart', this.onDragStart);
        this.scroller.addEventListener('touchmove', this.onTouchMove, { passive: true });
        this.resizeObserver.observe(this.scroller);
        this.scheduleLayout();
      }

      detach() {
        if (!this.scroller) return;
        this.scroller.removeEventListener('scroll', this.onScroll);
        this.scroller.removeEventListener('pointerdown', this.onPointerDown);
        this.scroller.removeEventListener('click', this.onClickCapture, true);
        this.scroller.removeEventListener('dragstart', this.onDragStart);
        this.scroller.removeEventListener('touchmove', this.onTouchMove);
        this.endDrag();
        this.resizeObserver?.unobserve(this.scroller);
        this.scroller = null;
      }

      items() {
        return this.scroller ? [...this.scroller.querySelectorAll('.instafeed-new-layout-item')] : [];
      }

      maxScroll() {
        return Math.max(this.scroller.scrollWidth - this.scroller.clientWidth, 0);
      }

      itemPositions() {
        const items = this.items();
        if (!items.length) return [];
        const max = this.maxScroll();
        const origin = items[0].offsetLeft;
        const positions = items.map((item) => Math.min(Math.round(item.offsetLeft - origin), max));
        positions.push(max);
        return [...new Set(positions)].sort((a, b) => a - b);
      }

      perView() {
        const items = this.items();
        if (items.length < 2) return 1;
        const step = items[1].offsetLeft - items[0].offsetLeft;
        const padding = parseFloat(getComputedStyle(this.scroller).paddingLeft) || 0;
        const view = this.scroller.clientWidth - padding * 2 + (step - items[0].offsetWidth);
        return Math.max(Math.floor(view / step), 1);
      }

      scheduleLayout() {
        cancelAnimationFrame(this.layoutFrame);
        this.layoutFrame = requestAnimationFrame(() => this.layout());
      }

      layout() {
        if (!this.scroller) return;
        const positions = this.itemPositions();
        const perView = this.perView();
        const max = this.maxScroll();
        const pages = positions.filter((position, index) => index % perView === 0 && position < max);
        if (max > 0 || !pages.length) pages.push(max);
        this.pages = [...new Set(pages)];
        this.renderDots();
        this.update();
      }

      renderDots() {
        if (!this.progress) return;
        const label = this.dataset.dotLabel || '';
        const count = this.pages.length > 1 ? this.pages.length : 0;
        if (this.progress.children.length === count) return;
        this.progress.replaceChildren(
          ...Array.from({ length: count }, (_, index) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'ig-feed__dot';
            dot.dataset.page = index;
            dot.setAttribute('aria-label', label.replace('[number]', index + 1));
            return dot;
          })
        );
      }

      activePage() {
        const current = this.scroller.scrollLeft;
        let active = 0;
        this.pages.forEach((position, index) => {
          if (Math.abs(position - current) < Math.abs(this.pages[active] - current)) active = index;
        });
        return active;
      }

      update() {
        if (!this.scroller) return;
        const max = this.maxScroll();
        const current = this.scroller.scrollLeft;
        const active = this.activePage();
        [...(this.progress?.children || [])].forEach((dot, index) => {
          const isActive = index === active;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
        this.arrows.forEach((arrow) => {
          const direction = Number(arrow.dataset.direction);
          arrow.disabled = direction < 0 ? current <= 1 : current >= max - 1;
        });
      }

      scrollToPosition(position) {
        this.scroller.scrollTo({ left: position, behavior: 'smooth' });
      }

      onArrowClick(event) {
        if (!this.scroller || !this.pages.length) return;
        const direction = Number(event.currentTarget.dataset.direction) || 1;
        const current = this.scroller.scrollLeft;
        const target =
          direction > 0
            ? this.pages.find((position) => position > current + 1)
            : [...this.pages].reverse().find((position) => position < current - 1);
        this.scrollToPosition(target ?? (direction > 0 ? this.maxScroll() : 0));
      }

      onDotClick(event) {
        const dot = event.target.closest('[data-page]');
        if (!dot || !this.scroller) return;
        const position = this.pages[Number(dot.dataset.page)];
        if (position !== undefined) this.scrollToPosition(position);
      }

      onScroll() {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }

      onPointerDown(event) {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.drag = {
          id: event.pointerId,
          startX: event.clientX,
          startScroll: this.scroller.scrollLeft,
          moved: false,
        };
        window.addEventListener('pointermove', this.onPointerMove);
        window.addEventListener('pointerup', this.onPointerUp);
        window.addEventListener('pointercancel', this.onPointerUp);
      }

      onPointerMove(event) {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const distance = event.clientX - this.drag.startX;
        if (!this.drag.moved && Math.abs(distance) < 6) return;
        if (!this.drag.moved) {
          this.drag.moved = true;
          clearTimeout(this.snapTimer);
          this.classList.add('is-dragging');
        }
        event.preventDefault();
        this.scroller.scrollLeft = this.drag.startScroll - distance;
      }

      onPointerUp(event) {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const { moved, startScroll } = this.drag;
        this.endDrag();
        if (!moved) return;
        this.justDragged = true;
        setTimeout(() => {
          this.justDragged = false;
        }, 0);

        const current = this.scroller.scrollLeft;
        const positions = this.itemPositions();
        const forward = current > startScroll;
        const target = forward
          ? positions.find((position) => position >= current) ?? this.maxScroll()
          : [...positions].reverse().find((position) => position <= current) ?? 0;
        this.scroller.scrollLeft = current;
        this.scrollToPosition(target);
        this.snapTimer = setTimeout(() => this.classList.remove('is-dragging'), 450);
      }

      endDrag() {
        window.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.onPointerUp);
        window.removeEventListener('pointercancel', this.onPointerUp);
        this.drag = null;
      }

      onClickCapture(event) {
        if (!this.justDragged) return;
        event.preventDefault();
        event.stopPropagation();
        this.justDragged = false;
      }
    }
  );
}
